(() => {
  if ('pupmdVslGateControllerV2' in document.documentElement.dataset) return;
  document.documentElement.dataset.pupmdVslGateControllerV2 = "";

  /** @type {boolean} */
  let revealed = false;

  /** @type {AbortController | null} */
  let abort = null;

  /** @returns {boolean} */
  const isDesignMode = () => Boolean(window.Shopify?.designMode);

  /**
   * @param {HTMLElement} phaseElement
   * @returns {HTMLElement}
   */
  const getSectionRoot = (phaseElement) => {
    if (phaseElement.dataset.pupmdVslScope === 'self') return phaseElement;

    const shopifySection = phaseElement.closest('.shopify-section');
    return shopifySection instanceof HTMLElement ? shopifySection : phaseElement;
  };

  /**
   * @param {HTMLElement} sectionRoot
   * @param {boolean} visible
   */
  const setSectionVisible = (sectionRoot, visible) => {
    sectionRoot.hidden = !visible;

    if (visible) {
      sectionRoot.removeAttribute('aria-hidden');
    } else {
      sectionRoot.setAttribute('aria-hidden', 'true');
    }

    for (const phaseElement of sectionRoot.querySelectorAll('[data-pupmd-vsl-phase]')) {
      if (!(phaseElement instanceof HTMLElement)) continue;
      phaseElement.hidden = !visible;

      if (visible) {
        phaseElement.removeAttribute('aria-hidden');
      } else {
        phaseElement.setAttribute('aria-hidden', 'true');
      }
    }
  };

  /**
   * @param {string} phase
   * @param {boolean} visible
   */
  const setPhaseVisible = (phase, visible) => {
    for (const phaseElement of document.querySelectorAll(`[data-pupmd-vsl-phase="${phase}"]`)) {
      if (!(phaseElement instanceof HTMLElement)) continue;
      setSectionVisible(getSectionRoot(phaseElement), visible);
    }
  };

  const ensureAllVisible = () => {
    setPhaseVisible('pre', true);
    setPhaseVisible('hidden', true);
    setPhaseVisible('always', true);
  };

  const syncInitialHiddenState = () => {
    for (const phaseElement of document.querySelectorAll('[data-pupmd-vsl-phase="hidden"]')) {
      if (!(phaseElement instanceof HTMLElement)) continue;
      if (!phaseElement.hidden) continue;
      setSectionVisible(getSectionRoot(phaseElement), false);
    }
  };

  const contractHero = () => {
    const innerElement = document.querySelector('.pupmd-vsl-always-player-v2 .pupmd-vsl-always-player-v2__inner');
    if (!(innerElement instanceof HTMLElement)) return;

    innerElement.classList.remove('is-playing');
  };

  const showScrollDown = () => {
    const scrollDown = document.querySelector('.pupmd-vsl-always-alert-v2 .scroll-down');
    if (!(scrollDown instanceof HTMLElement)) return;

    scrollDown.removeAttribute('aria-hidden');
  };

  const hideGetStartedLinks = () => {
    for (const getStartedLink of document.querySelectorAll('[data-pupmd-vsl-get-started]')) {
      if (!(getStartedLink instanceof HTMLElement)) continue;
      getStartedLink.hidden = true;
      const listItem = getStartedLink.closest('li');
      if (listItem instanceof HTMLElement) listItem.hidden = true;
    }
  };

  const syncOfferUrlParam = () => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('offer') === '1') return;

    url.searchParams.set('offer', '1');
    window.history.replaceState(window.history.state, '', url.toString());
  };

  /** @returns {boolean} */
  const shouldRevealFromUrl = () => new URLSearchParams(window.location.search).get('offer') === '1';

  /** @returns {boolean} */
  const shouldRevealOnLoad = () => {
    const playerElement = document.querySelector('pupmd-vsl-always-player-v2');
    return playerElement instanceof HTMLElement && playerElement.hasAttribute('data-reveal-on-load');
  };

  /**
   * ClickBank attribution: hop.min.js (loaded only on the ClickBank template) exposes finishHop(),
   * which appends the Hop ID to all ClickBank payment links. Call it once the links are revealed.
   * hop.min.js is deferred, so if it is not ready yet (e.g. ?offer=1), retry briefly until it is.
   */
  const finishClickbankHop = () => {
    if (typeof window.finishHop === 'function') {
      window.finishHop();
      return;
    }

    if (!document.querySelector('script[src*="scripts.clickbank.net/hop"]')) return;

    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      if (typeof window.finishHop === 'function') {
        window.clearInterval(timer);
        window.finishHop();
      } else if (attempts >= 40) {
        window.clearInterval(timer);
      }
    }, 250);
  };

  /**
   * @param {{ source: string, time?: number }} detail
   */
  const reveal = (detail) => {
    if (revealed || isDesignMode()) return;

    revealed = true;
    document.documentElement.dataset.pupmdVslState = 'revealed';

    if (detail.source !== 'setting') syncOfferUrlParam();

    setPhaseVisible('pre', false);
    setPhaseVisible('hidden', true);

    contractHero();
    showScrollDown();
    hideGetStartedLinks();
    finishClickbankHop();

    document.dispatchEvent(
      new CustomEvent('pupmd:vsl:revealed', {
        detail,
      }),
    );
  };

  const init = () => {
    abort?.abort();
    abort = new AbortController();
    const signal = abort.signal;

    if (isDesignMode()) {
      ensureAllVisible();
      document.addEventListener('shopify:section:load', ensureAllVisible, { signal });
      document.addEventListener('shopify:section:unload', ensureAllVisible, { signal });
      return;
    }

    syncInitialHiddenState();

    if (shouldRevealFromUrl()) {
      reveal({ source: 'query' });
    } else if (shouldRevealOnLoad()) {
      reveal({ source: 'setting' });
    }

    document.addEventListener(
      'pupmd:vsl:reveal',
      (event) => {
        if (!(event instanceof CustomEvent)) return;

        const source = typeof event.detail?.source === 'string' ? event.detail.source : 'wistia';
        const time = typeof event.detail?.time === 'number' ? event.detail.time : undefined;

        reveal({ source, time });
      },
      { signal },
    );

    document.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) return;

        const getStartedLink = event.target.closest('[data-pupmd-vsl-get-started]');
        if (!(getStartedLink instanceof HTMLElement)) return;

        event.preventDefault();

        document.dispatchEvent(
          new CustomEvent('pupmd:vsl:reveal', {
            detail: { source: 'manual' },
          }),
        );
      },
      { signal },
    );
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
